import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateOnly } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import { openStoredQuotePdf, quoteStatusColor, quoteStatusLabel } from './quoteUtils';

const formatDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Every sent quote version of a work order, newest first.
 *
 * `dense` is the one-line variant inside the quote dialog; the full variant (in
 * QuoteHistoryDialog) adds sender, validity, the customer's answer and note.
 */
function QuoteVersionList({ quotes, dense = false, onError, 'data-testid': testId }) {
  const { t } = useTranslation();

  if (!quotes || quotes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={testId}>
        {t('quoteSnapshot.noSentQuotes')}
      </Typography>
    );
  }

  const handleOpenPdf = async (quote) => {
    try {
      await openStoredQuotePdf(quote);
    } catch (err) {
      if (onError) onError(t('Failed to generate offer'));
    }
  };

  return (
    <Stack spacing={dense ? 1 : 1.5} data-testid={testId}>
      {quotes.map((quote, index) => {
        const answered = quote.status === 'accepted' || quote.status === 'declined';
        const recipient = [quote.recipient_email, quote.recipient_phone].filter(Boolean).join(', ');
        return (
          <Box
            key={quote.id}
            data-testid="quote-version-row"
            sx={{
              p: dense ? 1 : 1.5,
              borderRadius: 1.5,
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: quote.status === 'accepted' ? 'success.light' : 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                {formatDate(quote.sent_at)}
              </Typography>
              {index === 0 && (
                <Chip size="small" color="primary" label={t('quoteSnapshot.newest')} sx={{ height: 20, fontSize: 11 }} />
              )}
              {!dense && quote.document_type === 'kustannusarvio' && (
                <Typography variant="body2" color="text.secondary">{t('quoteSnapshot.pageTitleCostEstimate')}</Typography>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {recipient}
              </Typography>
              <Typography variant="body2" fontWeight={dense ? 400 : 600}>
                {formatPriceWithCurrency(quote.total_incl_vat)}
              </Typography>
              <Chip size="small" variant="outlined" color={quoteStatusColor(quote)} label={quoteStatusLabel(quote, t)} />
              <Tooltip title={t('quoteSnapshot.openPdf')}>
                <IconButton size="small" onClick={() => handleOpenPdf(quote)}>
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!dense && (
                <Tooltip title={t('quoteSnapshot.openCustomerPage')}>
                  <IconButton size="small" component="a" href={quote.public_url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {!dense && (
              <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {quote.sent_by_name && <span>{t('quoteSnapshot.sentBy')}: {quote.sent_by_name}</span>}
                  {quote.email_sent && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                      <EmailIcon sx={{ fontSize: 14 }} /> {t('quoteSnapshot.channelEmail')}
                    </Box>
                  )}
                  {quote.sms_sent && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                      <SmsIcon sx={{ fontSize: 14 }} /> {t('quoteSnapshot.channelSms')}
                    </Box>
                  )}
                  {quote.valid_until && <span>{t('quoteSnapshot.validUntil', { date: formatDateOnly(quote.valid_until) })}</span>}
                  {!quote.acceptance_requested && <span>{t('quoteSnapshot.acceptanceNotRequested')}</span>}
                </Typography>
                {answered && (
                  <Typography
                    variant="body2"
                    sx={{ color: quote.status === 'accepted' ? 'success.dark' : 'error.dark', fontWeight: 500 }}
                  >
                    {quote.status === 'accepted'
                      ? t('quoteSnapshot.acceptedByCustomer', { date: formatDateTime(quote.responded_at) })
                      : t('quoteSnapshot.declinedByCustomer', { date: formatDateTime(quote.responded_at) })}
                  </Typography>
                )}
                {answered && quote.customer_note && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {t('quoteSnapshot.customerNote')}: {quote.customer_note}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

export default QuoteVersionList;
